<template>
  <div
    ref="list"
    class="v-select"
    :class="{
      [layout]: true,
      [extraClassRoot]: !!extraClassRoot,
      ['no-select']: !canSelect,
      ['has-error']: errors.length
    }"
  >
    <div v-if="label" class="v-select__label">
      {{ this.label }}
      <span v-if="required" class="v-select__required">(Required)</span>
    </div>
    <div
      class="v-select__wrapper"
      :class="{
        [extraClass]: true,
        ['expanded']: listExpanded,
        [type]: !!type
      }"
    >
      <div class="v-select__current-value" @click="toggleList">
        <input
          v-if="inputActive"
          type="text"
          ref="input"
          :value="currentValue"
          @click.capture="_onInputClick"
          @input="_handleInput"
        />
        <span :class="{ ['no-sel']: !canSelect }" v-else>
          <span v-if="getTitle(value)">{{ getTitle(value) }}</span>
          <span v-else class="placeholder">{{ placeholder }}</span>
        </span>
        <i v-if="type === 'classic' && canSelect" :class="getIconClass"> </i>
        <i v-else-if="selectIcon && canSelect" :class="`far fa-${selectIcon}`">
        </i>
      </div>
      <div
        v-if="!listExpanded"
        v-for="(error, idx) in errors"
        :key="idx"
        class="error"
      >
        {{ error }}
      </div>
      <ul
        v-show="listExpanded"
        :class="{ [dropDownWidth]: true, [position]: true }"
      >
        <li
          v-for="option in localOptions"
          :key="option.key"
          :class="{ ['active']: option.value === value }"
          @click="e => selectOption(e, option)"
        >
          {{ option.selectLabel || option.label }}
        </li>

        <li class="no-data" v-if="localOptions && !localOptions.length">
          No data found
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

import cloneDeep from "lodash/cloneDeep";

export default Vue.component("VSelect", {
  props: {
    options: VueTypes.arrayOf(
      VueTypes.shape({
        key: VueTypes.key,
        label: VueTypes.string,
        value: VueTypes.oneOfType([VueTypes.string, VueTypes.number])
      })
    ).required,
    extraClass: VueTypes.string.def(""),
    extraClassRoot: VueTypes.string.def(""),
    errors: VueTypes.arrayOf(VueTypes.string).def([]),

    // current selected value
    value: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
    label: VueTypes.string,
    layout: VueTypes.oneOf(["half", "full", "auto"]).def("full"),
    placeholder: VueTypes.string.def("Select"),
    dropDownWidth: VueTypes.oneOf(["full", "fitContent"]).def("full"),
    position: VueTypes.oneOf(["up", "down"]).def("down"),
    type: VueTypes.oneOf(["classic", "alternate"]).def("classic"),

    // font awesome icon to place next to label
    selectIcon: VueTypes.string,

    withFilter: VueTypes.bool.def(false),
    canSelect: VueTypes.bool.def(true),
    required: VueTypes.bool.def(false)
  },

  data() {
    return {
      currentValue: this.getTitle(this.value),
      localOptions: Array.from(this.options),
      listExpanded: false,
      inputActive: false
    };
  },

  methods: {
    selectOption(e, option) {
      e.stopPropagation();
      this.$emit("select", e, option, this.value);
      this.listExpanded = false;
      this.localOptions = this.options;
    },

    toggleList() {
      if (!this.canSelect) {
        return;
      }

      this.listExpanded = !this.listExpanded;
    },

    closeList(e) {
      if (!this.listExpanded) {
        return;
      }

      const listEl = this.$refs.list;
      const target = e.target;

      if (listEl !== target && !listEl.contains(target)) {
        this.listExpanded = false;
      }
    },

    getTitle(value) {
      const item = this.options.filter(item => item.value === value)[0];
      return item ? item.label : "";
    },

    _handleInput(e) {
      this.currentValue = e.target.value;

      this.localOptions = this.options.filter(item =>
        item.label.toLowerCase().includes(this.currentValue.toLowerCase())
      );

      if (this.currentValue === "") {
        this.localOptions = this.options;
      }
    },

    _onInputClick(e) {
      e.stopPropagation();
    }
  },

  computed: {
    getIconClass() {
      let base = "far fa-chevron-";

      if (this.listExpanded) {
        base = `${base}${this.position === "down" ? "up" : "down"}`;
      } else {
        base = `${base}${this.position === "down" ? "down" : "up"}`;
      }

      return base;
    }
  },

  watch: {
    listExpanded: function(value) {
      this.inputActive = this.withFilter && value;

      if (this.inputActive) {
        setTimeout(() => this.$refs.input.focus(), 100);
      }
    },

    options: function(value) {
      this.localOptions = value;
    }
  },

  created() {
    document.body.addEventListener("click", this.closeList, true);
  },

  destroyed() {
    document.body.removeEventListener("click", this.closeList, true);
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-select";
$dropdown-main-color: #a3adb6;
$select-height: $input-height;

.error {
  width: 100%;
  font-size: 14px;
  padding-top: 7px;
  font-family: Lato-Light, sans-serif;
  text-align: left;

  &:nth-child(1) {
    padding: 0.7em 0.1em;
  }

  &,
  &:focus {
    color: $danger-color;
  }
}

.#{$ns} {
  &.has-error {
    .v-select__current-value {
      border-color: $danger-color;
    }
  }

  &__required {
    font-size: 15px;
    font-family: Lato-Light, sans-serif;
  }

  &.full {
    width: 100%;
  }

  &.half {
    width: calc(50% - 1em);
  }

  &.auto {
    width: auto;
  }

  &.no-select {
    .alternate {
      .v-select__current-value {
        cursor: default;

        &:hover {
          .no-sel span {
            color: $secondary-text-color;
          }
        }
      }
    }
  }

  &__label {
    @include textCropEllipsis();
    font-size: 15px;
    width: 100%;
    padding-left: 8px;
    padding-bottom: 14px;
    color: $primary-text-color;
    font-family: Lato-Bold, sans-serif;
    font-weight: 300;
    text-align: left;
  }

  &__wrapper {
    position: relative;
    transition: background 0.1s linear;

    &:hover {
      /*background: #f4f8fe;*/
    }

    .v-select__current-value {
      display: flex;
      align-items: center;
      padding: 12px 9px;
      border: 1px solid $primary-border-color;
      border-radius: 4px;
      justify-content: space-between;
      cursor: pointer;
      height: $select-height;

      i {
        color: #979797;
        margin-left: 10px;
      }

      span {
        text-align: left;
        min-width: 90%;
        font-family: Lato-Regular, sans-serif;
        color: $primary-text-color;
        font-size: 15px;
        @include textCropEllipsis();
        line-height: 1.2;

        &.placeholder {
          font-family: "Lato-Light", sans-serif;
          font-style: italic;
          font-size: 16px;
          display: inline-block;
          min-width: 100%;
          text-align: left;

          color: #979797;
        }
      }

      input {
        border: none;
        outline: none;
        width: 100%;
        font-size: 15px;
        padding: 0;
        color: $primary-text-color;
      }
    }

    ul {
      position: absolute;
      margin-top: 3px;
      list-style: none;
      border-radius: 4px;
      color: $button-dark-color;
      background: $white;
      max-height: 200px;
      overflow: auto;
      z-index: 10;
      margin-bottom: 30px;
      min-height: $input-height;
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);

      &.fitContent {
        width: fit-content;
      }

      &.full {
        width: 100%;
      }

      &.up {
        bottom: 10px;
      }

      li {
        display: flex;
        align-items: center;
        padding: 0 9px;
        height: $input-height;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.05s linear;
        white-space: nowrap;
        color: $primary-text-color;
        @include textCropEllipsis();

        &.active,
        &:hover {
          &:not(.no-data) {
            background: $primary-color;
            color: $white;
          }
        }

        &.no-data {
          cursor: default;
        }
      }
    }

    &.expanded {
      background: #c5dcf0;

      .v-select__current-value {
        border-color: $primary-color;
      }
    }

    &.alternate {
      width: auto;

      .v-select__current-value {
        display: flex;
        justify-content: center;
        width: fit-content;
        border: none;
        background: transparent;
        padding: 0;

        i,
        span {
          color: $default-color;
        }

        i {
          margin-left: 5px;
        }

        &:hover {
          span,
          i {
            color: $primary-color;
          }
        }
      }

      &:hover {
        background: transparent;
      }

      ul {
        overflow: visible;

        li {
          padding: 0 11px;
          min-width: 92px;
          color: $secondary-text-color;

          &:first-child {
            border-radius: 4px 4px 0 0;
          }

          &:last-child {
            border-radius: 0 0 4px 4px;
          }

          &.active,
          &:hover {
            color: $primary-color;
            background-color: transparent;
          }
        }
      }
    }

    &.expanded {
      background: transparent;
    }
  }
}
</style>
